/**
 * MessageList Component - Lista de mensagens do chat
 * @module features/chat/components/MessageList
 *
 * Componente container para exibir todas as mensagens do chat
 * com auto-scroll e indicador de digitacao.
 *
 * T002-FE: Interface de Chat Conversacional
 */

import { cn } from '@/lib/utils';
import { useEffect, useRef } from 'react';
import type { MessageListProps } from '../types/chat.types';
import { MessageBubble } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Lista rolavel de mensagens do chat
 *
 * Features:
 * - Auto-scroll para novas mensagens
 * - Indicador de digitacao integrado
 * - aria-live para anuncio de novas mensagens
 * - Layout flex column com gap consistente
 *
 * @example
 * ```tsx
 * <MessageList messages={messages} isTyping={isLoading} />
 * ```
 */
export function MessageList({ messages, isTyping = false }: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevMessagesLength = useRef(messages.length);

  // Obter conteudo da ultima mensagem para detectar mudancas durante streaming
  const lastMessage = messages[messages.length - 1];
  const lastMessageContent = lastMessage?.content || '';

  // Auto-scroll quando novas mensagens sao adicionadas ou conteudo muda (streaming)
  useEffect(() => {
    const isNewMessage = messages.length > prevMessagesLength.current;

    // Scroll se: nova mensagem adicionada OU streaming ativo (isTyping + conteudo mudando)
    if ((isNewMessage || isTyping) && bottomRef.current) {
      bottomRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }

    prevMessagesLength.current = messages.length;
  }, [messages.length, isTyping, lastMessageContent]);

  return (
    <div
      data-testid="message-list"
      role="log"
      aria-live="polite"
      aria-label="Mensagens do chat"
      aria-relevant="additions"
      className={cn('flex flex-col flex-1 gap-4 p-4 overflow-y-auto')}
    >
      {messages.map((message, index) => (
        <MessageBubble
          key={message.id}
          message={message}
          // Ultimo assistant message que ainda esta sendo escrita
          isStreaming={message.role === 'assistant' && index === messages.length - 1 && isTyping}
        />
      ))}

      <TypingIndicator
        isVisible={
          isTyping && messages.length > 0 && messages[messages.length - 1]?.role === 'user'
        }
      />

      {/* Spacer + elemento de scroll para compensar o input sticky */}
      {/* Altura: input (~56px) + padding do container (~24px) = ~80px */}
      <div ref={bottomRef} className="h-20 shrink-0" aria-hidden="true" />
    </div>
  );
}

export default MessageList;
